import React, { useState, useEffect } from 'react';
import { 
  FileEdit, 
  ChevronRight, 
  ChevronLeft,
  Building2,
  FileText,
  Table,
  Sparkles,
  Check,
  AlertCircle,
  Loader2,
  Download,
  Eye,
  RefreshCw,
  Plus,
  X,
  Zap,
  Target,
  BookOpen,
  Star,
  TrendingUp,
  Calendar,
  DollarSign,
  Users,
  ExternalLink,
  Info,
  Award,
  Clock
} from 'lucide-react';
import CompanyDataAuthorizationPanel from '../components/CompanyDataAuthorizationPanel';
import {
  isAuthorizationRequiredError,
  readApiResponseError,
} from '../services/companyDataAuthorizationService';

type Step = 'select-grant' | 'select-company' | 'context-table' | 'generate' | 'preview';

interface ContextTableRow {
  id: number;
  category: string;
  item: string;
  content: string;
  source: string;
  isAIGenerated: boolean;
}

interface EvaluationResult {
  total_score: number;
  max_score: number;
  percentage: number;
  grade: string;
  criteria_scores: Array<{
    category: string;
    item: string;
    max_score: number;
    score: number;
    status: string;
  }>;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

// API Base URL 동적 감지 - 매번 현재 location 확인
const getApiBaseDynamic = (): string => {
  // 환경 변수는 무시하고 항상 현재 호스트 기반으로 동적 결정
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const remoteUrl = `${protocol}//${hostname}:8001/api`;
    return remoteUrl;
  }
  return 'http://localhost:8001/api';
};

interface GrantDetail {
  id: string;
  title: string;
  source: string;
  deadline: string;
  budget: string;
  description?: string;
  ministry?: string;
  manageAgency?: string;
  category?: string;
  eligibility?: string[];
  requirements?: string[];
  evaluationCriteria?: string[];
  projectStartDate?: string;
  projectEndDate?: string;
  url?: string;
}

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('proposalcraft_access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const ProposalGenerator: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>('select-grant');
  const [selectedGrant, setSelectedGrant] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [authorizationId, setAuthorizationId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [proposalContent, setProposalContent] = useState<string>('');
  const [evaluation, setEvaluation] = useState<EvaluationResult | null>(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedProposalId, setSavedProposalId] = useState<string | null>(null);
  const [selectedGrantDetail, setSelectedGrantDetail] = useState<GrantDetail | null>(null);
  const [showGrantDetailModal, setShowGrantDetailModal] = useState(false);
  
  // API에서 가져온 데이터 상태
  const [grants, setGrants] = useState<GrantDetail[]>([]);
  const [companies, setCompanies] = useState<Array<{id: string, name: string, type: string, industry: string, matchScore: number | null, isMatchScoreDummy?: boolean}>>([]);
  const [isLoadingGrants, setIsLoadingGrants] = useState(true);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);

  const [contextTable, setContextTable] = useState<ContextTableRow[]>([
    { id: 1, category: '사업개요', item: '사업명', content: '', source: '', isAIGenerated: false },
    { id: 2, category: '사업개요', item: '사업목적', content: '', source: '', isAIGenerated: false },
    { id: 3, category: '사업개요', item: '사업기간', content: '', source: '', isAIGenerated: false },
    { id: 4, category: '사업개요', item: '총사업비', content: '', source: '', isAIGenerated: false },
    { id: 5, category: '현황분석', item: '국내외 현황', content: '', source: '', isAIGenerated: false },
    { id: 6, category: '현황분석', item: '문제점 및 개선방안', content: '', source: '', isAIGenerated: false },
    { id: 7, category: '추진전략', item: '추진체계', content: '', source: '', isAIGenerated: false },
    { id: 8, category: '추진전략', item: '세부 추진내용', content: '', source: '', isAIGenerated: false },
    { id: 9, category: '기대효과', item: '정량적 효과', content: '', source: '', isAIGenerated: false },
    { id: 10, category: '기대효과', item: '정성적 효과', content: '', source: '', isAIGenerated: false },
    { id: 11, category: '예산계획', item: '비목별 예산', content: '', source: '', isAIGenerated: false },
    { id: 12, category: '위험관리', item: '위험요인 및 대응방안', content: '', source: '', isAIGenerated: false },
  ]);

  const requireAuthorization = (): string | null => {
    if (!authorizationId) {
      setError(
        '선택한 기업의 자료 처리 동의가 필요합니다. 기업 선택 단계에서 활성 동의를 선택하거나 새 동의를 등록해 주세요.',
      );
      return null;
    }
    return authorizationId;
  };

  const invalidateAuthorizationIfNeeded = (
    apiError: { status: number; code?: string; message: string } | null,
  ): boolean => {
    const invalid =
      apiError !== null && isAuthorizationRequiredError(apiError);
    if (invalid) setAuthorizationId(null);
    return invalid;
  };

  const selectCompany = (companyId: string) => {
    setSelectedCompany(companyId);
    setAuthorizationId(null);
    setSavedProposalId(null);
    setError(null);
  };

  // 공모사업 목록 API 호출
  useEffect(() => {
    const fetchGrants = async () => {
      setIsLoadingGrants(true);
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${getApiBaseDynamic()}/public-grants/?limit=50`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error('공모사업 목록을 가져오는데 실패했습니다');
        }

        const data = await response.json();
        
        if (data.items && Array.isArray(data.items)) {
          // API 응답을 GrantDetail 형식으로 변환
          const convertedGrants: GrantDetail[] = data.items.map((item: any) => ({
            id: item.id,
            title: item.title || '',
            source: item.source || item.source_name || '',
            deadline: item.deadline || item.d_day ? (typeof item.deadline === 'string' ? item.deadline.split('T')[0] : new Date(item.deadline).toISOString().split('T')[0]) : '',
            budget: item.budget || item.total_budget || item.support_amount || '',
            description: item.description || '',
            ministry: item.ministry || '',
            manageAgency: item.manage_agency || '',
            category: item.category || '',
            eligibility: Array.isArray(item.eligibility) ? item.eligibility : [],
            requirements: Array.isArray(item.requirements) ? item.requirements : [],
            evaluationCriteria: Array.isArray(item.evaluation_criteria) ? item.evaluation_criteria : [],
            projectStartDate: item.project_start_date ? (typeof item.project_start_date === 'string' ? item.project_start_date.split('T')[0] : new Date(item.project_start_date).toISOString().split('T')[0]) : undefined,
            projectEndDate: item.project_end_date ? (typeof item.project_end_date === 'string' ? item.project_end_date.split('T')[0] : new Date(item.project_end_date).toISOString().split('T')[0]) : undefined,
            url: item.url || item.detail_url || undefined,
          }));
          setGrants(convertedGrants);
        } else {
          setGrants([]);
        }
      } catch (err) {
        console.error('Failed to fetch grants:', err);
        setError('공모사업 목록을 불러오는데 실패했습니다');
        setGrants([]);
      } finally {
        setIsLoadingGrants(false);
      }
    };

    fetchGrants();
  }, []);

  // 기업 목록 API 호출
  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${getApiBaseDynamic()}/companies/my/companies`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error('기업 목록을 가져오는데 실패했습니다');
        }

        const data = await response.json();
        
        if (data.items && Array.isArray(data.items)) {
          // API 응답을 컴포넌트에서 사용하는 형식으로 변환
          const convertedCompanies = data.items.map((item: any) => ({
            id: item.id,
            name: item.name || '',
            type: item.company_type || '',
            industry: item.industry || '',
            // matchScore: 실제 API에서 제공하면 사용, 없으면 null
            matchScore: item.match_score ?? item.matchScore ?? null, // null이면 아직 계산 안됨
            isMatchScoreDummy: !(item.match_score || item.matchScore),
          }));
          setCompanies(convertedCompanies);
        } else {
          setCompanies([]);
        }
      } catch (err) {
        console.error('Failed to fetch companies:', err);
        setError('기업 목록을 불러오는데 실패했습니다');
        setCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);


  const steps = [
    { id: 'select-grant', label: '공모사업 선택', icon: FileText },
    { id: 'select-company', label: '기업 선택', icon: Building2 },
    { id: 'context-table', label: '맥락표 작성', icon: Table },
    { id: 'generate', label: 'AI 생성', icon: Sparkles },
    { id: 'preview', label: '미리보기', icon: Eye },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const handleNext = () => {
    const stepOrder: Step[] = ['select-grant', 'select-company', 'context-table', 'generate', 'preview'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    const stepOrder: Step[] = ['select-grant', 'select-company', 'context-table', 'generate', 'preview'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleGenerateContextTable = async () => {
    if (!selectedGrant || !selectedCompany) return;
    const selectedAuthorizationId = requireAuthorization();
    if (!selectedAuthorizationId) return;
    
    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);

    // 진행률 시뮬레이션
    let progressInterval: ReturnType<typeof setInterval> | null = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) {
          if (progressInterval) { clearInterval(progressInterval); progressInterval = null; }
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const response = await fetch(`${getApiBaseDynamic()}/proposals/context-table`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          grant_id: selectedGrant,
          company_id: selectedCompany,
          authorization_id: selectedAuthorizationId,
        }),
      });

      if (progressInterval) { clearInterval(progressInterval); progressInterval = null; }
      setGenerationProgress(100);

      if (!response.ok) {
        throw await readApiResponseError(
          response,
          '맥락표 생성에 실패했습니다.',
        );
      }

      const data = await response.json();
      
      if (data.context_table) {
        // API 응답을 맥락표 형식으로 변환
        const newContextTable = data.context_table.map((item: any, index: number) => ({
          id: index + 1,
          category: item.category,
          item: item.item,
          content: item.content,
          source: item.source || 'Claude API',
          isAIGenerated: item.is_ai_generated !== false,
        }));
        setContextTable(newContextTable);
      }
      
      setTimeout(() => {
        setIsGenerating(false);
        handleNext();
      }, 500);
      
    } catch (caught: unknown) {
      if (progressInterval) { clearInterval(progressInterval); progressInterval = null; }
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      setError(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 기업 선택 단계에서 동의를 확인해 주세요.'
          : apiError?.message || '맥락표 생성 중 오류가 발생했습니다.',
      );
      setGenerationProgress(0);
      setIsGenerating(false);
    }
  };

  const handleGenerateProposal = async () => {
    if (!selectedGrant || !selectedCompany) return;
    const selectedAuthorizationId = requireAuthorization();
    if (!selectedAuthorizationId) return;

    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);

    let progressInterval2: ReturnType<typeof setInterval> | null = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) {
          if (progressInterval2) { clearInterval(progressInterval2); progressInterval2 = null; }
          return 90;
        }
        return prev + 5;
      });
    }, 200);

    try {
      const response = await fetch(`${getApiBaseDynamic()}/proposals/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          grant_id: selectedGrant,
          company_id: selectedCompany,
          authorization_id: selectedAuthorizationId,
          context_table: contextTable.map(row => ({
            category: row.category,
            item: row.item,
            content: row.content,
            source: row.source,
            is_ai_generated: row.isAIGenerated,
          })),
        }),
      });

      if (progressInterval2) { clearInterval(progressInterval2); progressInterval2 = null; }
      setGenerationProgress(100);

      if (response.ok) {
        const data = await response.json();
        setProposalContent(data.content || '');
        if (data.proposal_id) setSavedProposalId(data.proposal_id);
      } else {
        throw await readApiResponseError(
          response,
          '제안서 생성에 실패했습니다.',
        );
      }

      setTimeout(() => {
        setIsGenerating(false);
        handleNext();
      }, 500);

    } catch (caught: unknown) {
      if (progressInterval2) { clearInterval(progressInterval2); progressInterval2 = null; }
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      setError(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 기업 선택 단계에서 동의를 확인해 주세요.'
          : apiError?.message || '제안서 생성 중 오류가 발생했습니다.',
      );
      setIsGenerating(false);
    }
  };

  const handleEvaluate = async () => {
    if (!selectedGrant || !selectedCompany || !proposalContent) return;
    const selectedAuthorizationId = requireAuthorization();
    if (!selectedAuthorizationId) return;

    setIsEvaluating(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseDynamic()}/proposals/evaluate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          content: proposalContent,
          grant_id: selectedGrant,
          company_id: selectedCompany,
          authorization_id: selectedAuthorizationId,
          save_result: false,
        }),
      });

      if (!response.ok) {
        throw await readApiResponseError(response, '평가에 실패했습니다.');
      }

      const data = await response.json();
      setEvaluation(data.evaluation || data);
      setShowEvaluation(true);

    } catch (caught: unknown) {
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      setError(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 기업 선택 단계에서 동의를 확인해 주세요.'
          : apiError?.message || 'AI 평가에 실패했습니다. 다시 시도해주세요.',
      );
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleSaveProposal = async () => {
    if (!selectedGrant || !selectedCompany || !proposalContent) return;
    const selectedAuthorizationId = requireAuthorization();
    if (!selectedAuthorizationId) return;

    setIsSaving(true);
    setError(null);

    try {
      const selectedGrantData = grants.find(g => g.id === selectedGrant);

      const response = await fetch(`${getApiBaseDynamic()}/proposals/${savedProposalId || 'new'}/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: selectedGrantData?.title ? `${selectedGrantData.title} 제안서` : '새 제안서',
          content: proposalContent,
          context_table: contextTable.map(row => ({
            category: row.category,
            item: row.item,
            content: row.content,
          })),
          grant_id: selectedGrant,
          company_id: selectedCompany,
          authorization_id: selectedAuthorizationId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSavedProposalId(data.proposal_id || data.id);
        alert('제안서가 저장되었습니다.');
      } else {
        throw await readApiResponseError(response, '저장에 실패했습니다.');
      }
    } catch (caught: unknown) {
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      setError(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 기업 선택 단계에서 동의를 확인해 주세요.'
          : apiError?.message || '제안서 저장에 실패했습니다.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadProposal = async () => {
    if (!proposalContent || !selectedCompany) return;
    const selectedAuthorizationId = requireAuthorization();
    if (!selectedAuthorizationId) return;

    try {
      const selectedGrantData = grants.find(g => g.id === selectedGrant);
      const title = selectedGrantData?.title || '제안서';
      const response = await fetch(
        `${getApiBaseDynamic()}/proposals/export/document`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            proposal_data: {
              title,
              content: proposalContent,
              company_name:
                companies.find((company) => company.id === selectedCompany)
                  ?.name || '',
              sections: contextTable.map((row) => row.category),
            },
            company_id: selectedCompany,
            authorization_id: selectedAuthorizationId,
            format: 'markdown',
          }),
        },
      );
      if (!response.ok) {
        throw await readApiResponseError(
          response,
          '제안서 내보내기에 실패했습니다.',
        );
      }
      const data = await response.json();
      const blob = data.is_base64
        ? new Blob(
            [
              Uint8Array.from(atob(data.content), (char) =>
                char.charCodeAt(0),
              ),
            ],
            { type: data.content_type || 'text/markdown;charset=utf-8' },
          )
        : new Blob([data.content], {
            type: data.content_type || 'text/markdown;charset=utf-8',
          });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        data.filename ||
        `${title.replace(/[^a-zA-Z0-9가-힣]/g, '_')}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (caught: unknown) {
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      setError(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 기업 선택 단계에서 동의를 확인해 주세요.'
          : apiError?.message || '다운로드에 실패했습니다.',
      );
    }
  };

  const updateContextTableRow = (id: number, field: 'content' | 'source', value: string) => {
    setContextTable(prev => prev.map(row => 
      row.id === id ? { ...row, [field]: value, isAIGenerated: false } : row
    ));
  };

  const getSourceColor = (source: string) => {
    const colors: { [key: string]: string } = {
      'NIPA': 'bg-purple-100 text-purple-800',
      'NIA': 'bg-blue-100 text-blue-800',
      '콘텐츠진흥원': 'bg-pink-100 text-pink-800',
      '산자부': 'bg-green-100 text-green-800',
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-emerald-600 bg-emerald-50';
      case 'good': return 'text-blue-600 bg-blue-50';
      case 'average': return 'text-yellow-600 bg-yellow-50';
      case 'poor': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'from-emerald-500 to-teal-500';
      case 'B': return 'from-blue-500 to-cyan-500';
      case 'C': return 'from-yellow-500 to-orange-500';
      case 'D': return 'from-red-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">사업계획서 작성</h1>
          <p className="text-gray-600 mt-2">AI 맥락표 기반 사업계획서 자동 생성</p>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* 진행 단계 표시 */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    isActive ? 'bg-emerald-600 text-white' :
                    isCompleted ? 'bg-emerald-100 text-emerald-600' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {isCompleted ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                  </div>
                  <span className={`mt-2 text-sm font-medium ${
                    isActive ? 'text-emerald-600' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${
                    index < currentStepIndex ? 'bg-emerald-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* 단계별 콘텐츠 */}
      <div className="card min-h-[500px]">
        {/* 공모사업 선택 */}
        {currentStep === 'select-grant' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FileText className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">공모사업 선택</h2>
            </div>
            <p className="text-gray-600">사업계획서를 작성할 공모사업을 선택하세요.</p>
            
            {isLoadingGrants ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-12 w-12 text-emerald-600 animate-spin mb-4" />
                <p className="text-gray-600">공모사업 목록을 불러오는 중...</p>
              </div>
            ) : grants.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
                <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">등록된 공모사업이 없습니다</p>
                <p className="text-sm text-gray-500">관리자에게 문의하거나 나중에 다시 시도해주세요</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {grants.map((grant) => (
                <div
                  key={grant.id}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    selectedGrant === grant.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSourceColor(grant.source)}`}>
                      {grant.source}
                    </span>
                    {selectedGrant === grant.id && (
                      <Check className="h-5 w-5 text-emerald-600" />
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{grant.title}</h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <span>마감: {grant.deadline}</span>
                    <span>예산: {grant.budget}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedGrantDetail(grant);
                        setShowGrantDetailModal(true);
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                    >
                      <Info className="h-4 w-4 mr-1" />
                      상세보기
                    </button>
                    <button
                      onClick={() => setSelectedGrant(grant.id)}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors flex items-center justify-center ${
                        selectedGrant === grant.id
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                      }`}
                    >
                      {selectedGrant === grant.id ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          선택됨
                        </>
                      ) : (
                        '선택'
                      )}
                    </button>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 기업 선택 */}
        {currentStep === 'select-company' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <Building2 className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">기업/기관 선택</h2>
            </div>
            <p className="text-gray-600">사업계획서를 작성할 기업/기관을 선택하세요. AI가 매칭률을 분석했습니다.</p>
            
            {isLoadingCompanies ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-12 w-12 text-emerald-600 animate-spin mb-4" />
                <p className="text-gray-600">기업 목록을 불러오는 중...</p>
              </div>
            ) : companies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
                <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">등록된 기업이 없습니다</p>
                <p className="text-sm text-gray-500">관리자에게 문의하거나 나중에 다시 시도해주세요</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {companies.map((company) => (
                <div
                  key={company.id}
                  onClick={() => selectCompany(company.id)}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    selectedCompany === company.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center text-white font-bold">
                        {company.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{company.name}</h3>
                        <p className="text-sm text-gray-500">{company.type} · {company.industry}</p>
                      </div>
                    </div>
                    {selectedCompany === company.id && (
                      <Check className="h-5 w-5 text-emerald-600" />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center space-x-1">
                      <Target className="h-4 w-4 text-emerald-500" />
                      {company.matchScore !== null ? (
                        <span className="text-sm font-medium text-emerald-600">
                          매칭률 {company.matchScore}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">매칭률 분석중...</span>
                      )}
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}

            {selectedCompany && (
              <CompanyDataAuthorizationPanel
                companyId={selectedCompany}
                value={authorizationId}
                onChange={setAuthorizationId}
              />
            )}
          </div>
        )}

        {/* 맥락표 작성 */}
        {currentStep === 'context-table' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Table className="h-6 w-6 text-emerald-600" />
                <h2 className="text-xl font-semibold text-gray-900">맥락표 작성</h2>
              </div>
              <button 
                onClick={handleGenerateContextTable}
                disabled={isGenerating || !authorizationId}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {isGenerating ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-5 w-5 mr-2" />
                )}
                AI 자동 생성
              </button>
            </div>
            <p className="text-gray-600">맥락표를 작성하면 AI가 사업계획서를 자동으로 생성합니다. AI 자동 생성 버튼을 클릭하여 빠르게 시작하세요.</p>
            
            {isGenerating && (
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-emerald-700">AI가 맥락표를 생성하고 있습니다...</span>
                  <span className="text-sm text-emerald-600">{generationProgress}%</span>
                </div>
                <div className="w-full bg-emerald-200 rounded-full h-2">
                  <div 
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${generationProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border">구분</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border">항목</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border w-1/2">내용</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border">출처</th>
                  </tr>
                </thead>
                <tbody>
                  {contextTable.map((row) => (
                    <tr key={row.id} className={row.isAIGenerated ? 'bg-emerald-50' : ''}>
                      <td className="px-4 py-3 border text-sm text-gray-700">{row.category}</td>
                      <td className="px-4 py-3 border text-sm text-gray-700">{row.item}</td>
                      <td className="px-4 py-3 border">
                        <textarea
                          value={row.content}
                          onChange={(e) => updateContextTableRow(row.id, 'content', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                          rows={2}
                          placeholder="내용을 입력하세요..."
                        />
                      </td>
                      <td className="px-4 py-3 border">
                        <div className="flex items-center space-x-1">
                          {row.isAIGenerated && (
                            <Sparkles className="h-4 w-4 text-emerald-500" />
                          )}
                          <span className="text-xs text-gray-500">{row.source || '-'}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AI 생성 */}
        {currentStep === 'generate' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <Sparkles className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">AI 사업계획서 생성</h2>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 text-center">
              {isGenerating ? (
                <div className="space-y-4">
                  <Loader2 className="h-16 w-16 text-emerald-600 mx-auto animate-spin" />
                  <h3 className="text-xl font-semibold text-gray-900">AI가 사업계획서를 생성하고 있습니다...</h3>
                  <p className="text-gray-600">맥락표를 분석하고 전문적인 내용을 작성합니다</p>
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">생성 진행률</span>
                      <span className="font-medium text-emerald-600">{generationProgress}%</span>
                    </div>
                    <div className="w-full bg-emerald-200 rounded-full h-3">
                      <div 
                        className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${generationProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl mx-auto flex items-center justify-center">
                    <Zap className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">사업계획서 생성 준비 완료</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    선택한 공모사업과 기업 정보, 맥락표를 기반으로<br />
                    Claude API가 전문적인 사업계획서를 생성합니다.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-6">
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-500">사용 API</p>
                      <p className="font-semibold text-gray-900">Claude + Perplexity</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-500">예상 페이지</p>
                      <p className="font-semibold text-gray-900">약 30페이지</p>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-500">예상 시간</p>
                      <p className="font-semibold text-gray-900">약 1분</p>
                    </div>
                  </div>

                  <button 
                    onClick={handleGenerateProposal}
                    disabled={!authorizationId}
                    className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-5 w-5 mr-2 inline" />
                    사업계획서 생성하기
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 미리보기 */}
        {currentStep === 'preview' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6 text-emerald-600" />
                <h2 className="text-xl font-semibold text-gray-900">사업계획서 미리보기</h2>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={handleEvaluate}
                  disabled={isEvaluating || !authorizationId}
                  className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
                >
                  {isEvaluating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Star className="h-4 w-4 mr-2" />
                  )}
                  AI 평가
                </button>
                <button 
                  onClick={() => {
                    setCurrentStep('generate');
                    setProposalContent('');
                  }}
                  className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  재생성
                </button>
                <button
                  onClick={handleDownloadProposal}
                  disabled={!authorizationId}
                  className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  다운로드
                </button>
              </div>
            </div>

            {/* 평가 결과 모달 */}
            {showEvaluation && evaluation && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    AI 가상 평가 결과
                  </h3>
                  <button 
                    onClick={() => setShowEvaluation(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${getGradeColor(evaluation.grade)} flex items-center justify-center text-white text-2xl font-bold`}>
                      {evaluation.grade}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">등급</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{evaluation.total_score}</p>
                    <p className="text-sm text-gray-500">/ {evaluation.max_score}점</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-600">{evaluation.percentage}%</p>
                    <p className="text-sm text-gray-500">달성률</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <TrendingUp className="h-6 w-6 text-emerald-500" />
                      <span className="text-lg font-semibold text-emerald-600">상위 20%</span>
                    </div>
                    <p className="text-sm text-gray-500">예상 순위</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-1" />
                      강점
                    </h4>
                    <ul className="space-y-1">
                      {evaluation.strengths.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="text-emerald-500 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <AlertCircle className="h-4 w-4 text-orange-500 mr-1" />
                      개선 필요
                    </h4>
                    <ul className="space-y-1">
                      {evaluation.weaknesses.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="text-orange-500 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-purple-200">
                  <h4 className="font-medium text-gray-900 mb-2">개선 권장사항</h4>
                  <div className="flex flex-wrap gap-2">
                    {evaluation.recommendations.map((rec, idx) => (
                      <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-purple-700 border border-purple-200">
                        {rec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* 문서 미리보기 */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-inner p-8 min-h-[600px]">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {grants.find(g => g.id === selectedGrant)?.title} 사업계획서
                  </h1>
                  <p className="text-gray-600">{companies.find(c => c.id === selectedCompany)?.name}</p>
                </div>

                <div className="space-y-6">
                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 border-b-2 border-emerald-500 pb-2 mb-4">
                      1. 사업 개요
                    </h2>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>■ 사업명:</strong> {contextTable.find(r => r.item === '사업명')?.content}</p>
                      <p><strong>■ 사업목적:</strong> {contextTable.find(r => r.item === '사업목적')?.content}</p>
                      <p><strong>■ 사업기간:</strong> {contextTable.find(r => r.item === '사업기간')?.content}</p>
                      <p><strong>■ 총사업비:</strong> {contextTable.find(r => r.item === '총사업비')?.content}</p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 border-b-2 border-emerald-500 pb-2 mb-4">
                      2. 현황 분석
                    </h2>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>■ 국내외 현황:</strong> {contextTable.find(r => r.item === '국내외 현황')?.content}</p>
                      <p><strong>■ 문제점 및 개선방안:</strong> {contextTable.find(r => r.item === '문제점 및 개선방안')?.content}</p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 border-b-2 border-emerald-500 pb-2 mb-4">
                      3. 추진 전략
                    </h2>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>■ 추진체계:</strong> {contextTable.find(r => r.item === '추진체계')?.content}</p>
                      <p><strong>■ 세부 추진내용:</strong></p>
                      <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded-lg text-sm">
                        {contextTable.find(r => r.item === '세부 추진내용')?.content}
                      </pre>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 border-b-2 border-emerald-500 pb-2 mb-4">
                      4. 기대 효과
                    </h2>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>■ 정량적 효과:</strong> {contextTable.find(r => r.item === '정량적 효과')?.content}</p>
                      <p><strong>■ 정성적 효과:</strong> {contextTable.find(r => r.item === '정성적 효과')?.content}</p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 border-b-2 border-emerald-500 pb-2 mb-4">
                      5. 예산 계획
                    </h2>
                    <div className="space-y-3 text-gray-700">
                      <p><strong>■ 비목별 예산:</strong> {contextTable.find(r => r.item === '비목별 예산')?.content}</p>
                    </div>
                  </section>

                  <section>
                    <h2 className="text-lg font-semibold text-gray-900 border-b-2 border-emerald-500 pb-2 mb-4">
                      6. 위험 관리
                    </h2>
                    <div className="space-y-3 text-gray-700">
                      <pre className="whitespace-pre-wrap text-sm">
                        {contextTable.find(r => r.item === '위험요인 및 대응방안')?.content}
                      </pre>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 공모사업 상세 모달 */}
      {showGrantDetailModal && selectedGrantDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center space-x-3">
                <div className={`px-3 py-1 text-sm font-medium rounded-full ${getSourceColor(selectedGrantDetail.source)}`}>
                  {selectedGrantDetail.source}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{selectedGrantDetail.title}</h2>
              </div>
              <button
                onClick={() => setShowGrantDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* 모달 내용 */}
            <div className="px-6 py-6 space-y-6">
              {/* 기본 정보 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">마감일</p>
                    <p className="font-semibold text-gray-900">{selectedGrantDetail.deadline}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <DollarSign className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">예산 규모</p>
                    <p className="font-semibold text-gray-900">{selectedGrantDetail.budget}</p>
                  </div>
                </div>
                {selectedGrantDetail.ministry && (
                  <div className="flex items-start space-x-3">
                    <Building2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">주관부처</p>
                      <p className="font-semibold text-gray-900">{selectedGrantDetail.ministry}</p>
                    </div>
                  </div>
                )}
                {selectedGrantDetail.manageAgency && (
                  <div className="flex items-start space-x-3">
                    <Award className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">관리기관</p>
                      <p className="font-semibold text-gray-900">{selectedGrantDetail.manageAgency}</p>
                    </div>
                  </div>
                )}
                {selectedGrantDetail.category && (
                  <div className="flex items-start space-x-3">
                    <Target className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">분야</p>
                      <p className="font-semibold text-gray-900">{selectedGrantDetail.category}</p>
                    </div>
                  </div>
                )}
                {(selectedGrantDetail.projectStartDate || selectedGrantDetail.projectEndDate) && (
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">사업기간</p>
                      <p className="font-semibold text-gray-900">
                        {selectedGrantDetail.projectStartDate} ~ {selectedGrantDetail.projectEndDate}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* 사업 설명 */}
              {selectedGrantDetail.description && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FileText className="h-5 w-5 text-emerald-600 mr-2" />
                    사업 개요
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedGrantDetail.description}</p>
                </div>
              )}

              {/* 참여자격 */}
              {selectedGrantDetail.eligibility && selectedGrantDetail.eligibility.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Users className="h-5 w-5 text-emerald-600 mr-2" />
                    참여자격
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedGrantDetail.eligibility.map((item, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 신청요건 */}
              {selectedGrantDetail.requirements && selectedGrantDetail.requirements.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <BookOpen className="h-5 w-5 text-emerald-600 mr-2" />
                    신청요건
                  </h3>
                  <ul className="space-y-2">
                    {selectedGrantDetail.requirements.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-emerald-600 mr-2">•</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 평가기준 */}
              {selectedGrantDetail.evaluationCriteria && selectedGrantDetail.evaluationCriteria.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="h-5 w-5 text-emerald-600 mr-2" />
                    평가기준
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedGrantDetail.evaluationCriteria.map((item, index) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <p className="text-gray-700 font-medium">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 외부 링크 */}
              {selectedGrantDetail.url && (
                <div>
                  <a
                    href={selectedGrantDetail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    공고 페이지 보기
                  </a>
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowGrantDetailModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
              <button
                onClick={() => {
                  setSelectedGrant(selectedGrantDetail.id);
                  setShowGrantDetailModal(false);
                }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
              >
                <Check className="h-4 w-4 mr-2" />
                이 공모사업 선택하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 네비게이션 버튼 */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrev}
          disabled={currentStep === 'select-grant'}
          className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          이전
        </button>
        
        {currentStep !== 'preview' && currentStep !== 'generate' && (
          <button
            onClick={currentStep === 'context-table' ? handleGenerateContextTable : handleNext}
            disabled={
              (currentStep === 'select-grant' && !selectedGrant) ||
              (currentStep === 'select-company' &&
                (!selectedCompany || !authorizationId)) ||
              isGenerating
            }
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <Loader2 className="h-5 w-5 mr-1 animate-spin" />
            ) : currentStep === 'context-table' ? (
              <>
                <Sparkles className="h-5 w-5 mr-1" />
                AI 생성
              </>
            ) : (
              <>
                다음
                <ChevronRight className="h-5 w-5 ml-1" />
              </>
            )}
          </button>
        )}

        {currentStep === 'preview' && (
          <button
            onClick={handleSaveProposal}
            disabled={isSaving || !proposalContent || !authorizationId}
            className="flex items-center px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <Check className="h-5 w-5 mr-2" />
            )}
            {isSaving ? '저장 중...' : savedProposalId ? '업데이트' : '저장 및 완료'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProposalGenerator;
